import type { StoredCartProductItem } from './storageRecovery';
import type { SessionDataType } from './contexts/SessionData';

type SimpleOrderPlacementOrderData = Omit<SessionDataType, 'changeNeeded'> & {
  paymentMethod?: 'cash' | 'bank_card';
  changeNeeded?: 'Yes' | 'No';
};

type SimpleOrderPlacementPartnerDefaults = {
  pickupAddress: string;
  pickupPoint: [number, number] | null;
  location: string;
};

const SIMPLE_ORDER_PLACEMENT_PHONE_PREFIX = '0000';
const SIMPLE_ORDER_PLACEMENT_RANDOM_PHONE_MAX = 99_999_999;
const SIMPLE_ORDER_PLACEMENT_PHONE_PATTERN = /^0000(\d{8})$/;

/**
 * Generates the hidden wholesale checkout phone using eight random digits.
 * The zero value is excluded so a generated identity cannot look like a
 * placeholder even though leading zeroes in the eight-digit suffix are valid.
 */
export const generateSimpleOrderPlacementPhone = (
  random: () => number = Math.random,
): string => {
  const randomSuffix = Math.floor(random() * SIMPLE_ORDER_PLACEMENT_RANDOM_PHONE_MAX) + 1;
  return `${SIMPLE_ORDER_PLACEMENT_PHONE_PREFIX}${String(randomSuffix).padStart(8, '0')}`;
};

/**
 * Recognizes only phone values produced by the wholesale generator. This
 * keeps a browser's generated identity stable while rejecting the legacy
 * eight-digit placeholder and phone values stored by other checkout flows.
 */
export const isSimpleOrderPlacementPhone = (phone: string | null | undefined): boolean => {
  const match = phone?.match(SIMPLE_ORDER_PLACEMENT_PHONE_PATTERN);
  return Boolean(match && match[1] !== '00000000');
};

/**
 * Defaults fulfilment to delivery without replacing an explicit pickup choice.
 * Unknown persisted values are treated as missing so corrupted drafts cannot
 * silently keep the legacy pickup default.
 */
export const normalizeDeliveryNeeded = (value: string | null | undefined): 'Yes' | 'No' => (
  value === 'No' ? 'No' : 'Yes'
);

/**
 * Calculates the backend's total_products_cost contract from merchandise only.
 * Promotions and delivery charges are excluded because the backend applies
 * those after it has used this value to resolve delivery pricing.
 */
export const calculateTotalProductsCost = (
  shoppingCart: Array<Pick<StoredCartProductItem, 'total_cost' | 'quantity'>>,
): number => Number(
  shoppingCart
    .reduce((total, item) => total + (item.total_cost * item.quantity), 0)
    .toFixed(2),
);

/**
 * Supplies backend-required values for the temporary wholesale checkout while
 * preserving customer-entered identity, comments, and the currently resolved
 * channel. The partner pickup point acts as the hidden delivery destination.
 */
export const applySimpleOrderPlacementDefaults = <TOrderData extends SimpleOrderPlacementOrderData>(
  orderData: TOrderData,
  partnerDefaults: SimpleOrderPlacementPartnerDefaults,
  generatedPhone: string = generateSimpleOrderPlacementPhone(),
): TOrderData => ({
  ...orderData,
  contacts: {
    ...(orderData.contacts ?? {}),
    preferableMethod: 'phone',
  },
  countryCode: '+382',
  phone: isSimpleOrderPlacementPhone(orderData.phone) ? orderData.phone : generatedPhone,
  deliveryNeeded: 'Yes',
  paymentMethod: 'cash',
  changeNeeded: 'No',
  changeAmount: null,
  deliveryInTime: 'No',
  time: null,
  deliveryAddress: partnerDefaults.pickupAddress,
  loc_latitude: partnerDefaults.pickupPoint?.[0] ?? null,
  loc_longitude: partnerDefaults.pickupPoint?.[1] ?? null,
  location: partnerDefaults.location,
  loyalty_card_number: null,
} as TOrderData);
